import React, { useRef, useState } from "react";
import { Play, Pause, Check, Search, List, Plus } from "lucide-react";
import { useApp } from "../context/AppContext";
import SongListSheet from "./SongListSheet";

function AlbumCard({ song }) {
  const { current, playing, playSong, ownsSong, requestBuy } = useApp();
  const isCurrent = current && current.id === song.id;
  const isPlaying = isCurrent && playing;
  const owned = ownsSong(song.id);

  const handleBuy = () => requestBuy(song);

  return (
    <div className="snap-start shrink-0 w-[200px]">
      <div className="relative aspect-square rounded-2xl overflow-hidden group">
        <img src={song.cover} alt={song.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <button
          onClick={() => playSong(song)}
          className="absolute inset-0 flex items-center justify-center"
          aria-label="Play"
        >
          <span className="flex items-center justify-center w-12 h-12 rounded-full bg-white/90 text-black shadow-lg backdrop-blur transition-transform group-hover:scale-105 active:scale-95">
            {isPlaying ? <Pause size={20} /> : <Play size={20} className="ml-0.5" />}
          </span>
        </button>
        <span className="absolute top-2.5 left-2.5 text-[9px] tracking-[0.16em] uppercase px-2 py-0.5 rounded-full bg-black/55 text-white/80 backdrop-blur">
          {song.label}
        </span>
      </div>

      <div className="mt-2.5 flex items-end justify-between gap-2">
        <div className="min-w-0">
          <p className="font-cond font-semibold text-[15px] text-white truncate uppercase tracking-wide">{song.title}</p>
          <p className="text-[11px] text-white/40">{song.plays} plays</p>
        </div>
        {owned ? (
          <span className="flex items-center gap-1 text-[11px] text-[#8b5cf6] font-semibold whitespace-nowrap">
            <Check size={13} /> Owned
          </span>
        ) : (
          <button
            onClick={handleBuy}
            className="shrink-0 text-[12px] font-semibold px-3 h-7 rounded-full bg-white text-black hover:bg-white/85 transition-colors"
          >
            ${song.price.toFixed(2)}
          </button>
        )}
      </div>
    </div>
  );
}

export default function MusicSection() {
  const { artist } = useApp();
  const [query, setQuery] = useState("");
  const [sheet, setSheet] = useState(false);
  const scrollRef = useRef(null);

  const filtered = artist.songs.filter((s) =>
    s.title.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <section className="px-5 pt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="section-dot bg-[#8b5cf6]" />
          <h2 className="text-[13px] font-semibold tracking-[0.16em] uppercase text-white">Music</h2>
          <span className="text-[11px] text-white/35">{artist.songs.length} singles</span>
        </div>
        <button
          onClick={() => setSheet(true)}
          className="flex items-center gap-1 text-[12px] text-white/60 hover:text-white transition-colors"
        >
          <List size={14} /> See all
        </button>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-1 px-1 pb-1"
      >
        {filtered.map((song) => (
          <AlbumCard key={song.id} song={song} />
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-white/40 py-8">No songs match “{query}”.</p>
        )}
      </div>

      <div className="mt-4 flex items-center gap-2 px-4 h-11 rounded-full bg-white/[0.06] border border-white/10">
        <Search size={16} className="text-white/40" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search songs..."
          className="flex-1 bg-transparent outline-none text-[14px] text-white placeholder:text-white/35"
        />
        {query && (
          <button onClick={() => setQuery("")} className="text-white/40 text-xs hover:text-white">Clear</button>
        )}
      </div>

      <SongListSheet open={sheet} onClose={() => setSheet(false)} />
    </section>
  );
}
